<template>
    <div class="table-container-compact">
        <table class="table-compact">
            <thead>
                <tr>
                    <th style="text-align: center">조직</th>
                    <th style="text-align: center">시설</th>
                    <th style="text-align: center">카테고리</th>
                    <th style="text-align: center">시작월</th>
                    <th style="text-align: center">종료월</th>
                    <th style="text-align: center">수집 방법</th>
                    <th style="text-align: center">자동 수집</th>
                    <th style="text-align: center">등록일</th>
                    <th style="text-align: center"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td colspan="10" class="text-center"><i class="fas fa-spinner fa-spin"></i> 로딩 중...</td>
                </tr>
                <tr v-else-if="configs.length === 0">
                    <td colspan="10" class="text-center empty">설정이 없습니다</td>
                </tr>
                <tr
                    v-else
                    v-for="config in configs"
                    :key="config.configIdx"
                    :class="{ inactive: config.draftStat !== 'ACTIVE', 'clickable-row': true }"
                    @click="$emit('row-click', config)"
                    tabindex="0"
                    @keydown.enter="$emit('row-click', config)"
                    @keydown.space.prevent="$emit('row-click', config)"
                >
                    <td style="text-align: center">{{ config.organization?.orgName || "-" }}</td>
                    <td style="text-align: center">{{ config.facility?.facilityName || "-" }}</td>
                    <td style="text-align: center">
                        <span>
                            {{ config.category?.categoryName || "-" }}
                        </span>
                    </td>
                    <td style="text-align: center">{{ config.startMonth || "-" }}</td>
                    <td style="text-align: center">{{ config.endMonth || "무기한" }}</td>
                    <td style="text-align: center">
                        <span class="tag-small" :class="getMethodClass(config.collectionMethod)">
                            {{ getMethodLabel(config.collectionMethod) }}
                        </span>
                    </td>
                    <td style="text-align: center">
                        <span
                            v-if="config.isAutoCollect === 1"
                            class="auto-collect-badge ACTIVE"
                            :title="getAutoCollectInfo(config)"
                        >
                            활성화
                        </span>
                        <span v-else class="auto-collect-badge inactive">비활성화</span>
                    </td>
                    <td style="text-align: center">{{ formatDate(config.regDate) }}</td>
                    <td style="text-align: center" @click.stop>
                        <div class="action-buttons">
                            <button
                                class="icon-btn-mini"
                                @click.stop="$emit('toggle', config)"
                                :title="config.draftStat === 'ACTIVE' ? '비활성화' : '활성화'"
                                :aria-label="config.draftStat === 'ACTIVE' ? '비활성화' : '활성화'"
                            >
                                <i
                                    :class="config.draftStat === 'ACTIVE' ? 'fas fa-toggle-on' : 'fas fa-toggle-off'"
                                ></i>
                            </button>
                            <IconButton
                                icon="fas fa-copy"
                                size="small"
                                title="복사"
                                aria-label="설정 복사"
                                @click.stop="$emit('copy', config)"
                            />
                            <IconButton
                                icon="fas fa-edit"
                                size="small"
                                title="수정"
                                aria-label="설정 수정"
                                @click.stop="$emit('edit', config)"
                            />
                            <IconButton
                                icon="fas fa-trash"
                                size="small"
                                variant="delete"
                                title="삭제"
                                aria-label="설정 삭제"
                                @click.stop="$emit('delete', config)"
                            />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import CarbonCodes from "../../../codes";
import IconButton from "../../common/IconButton.vue";

export default {
    name: "ActivityConfigTable",
    components: { IconButton },
    props: {
        configs: { type: Array, default: () => [] },
        loading: { type: Boolean, default: false },
    },
    methods: {
        formatDate(date) {
            if (!date) return "-";
            return new Date(date).toLocaleDateString("ko-KR");
        },

        getMethodClass(method) {
            return CarbonCodes.collectionMethod.getMeta(method)?.class || "";
        },

        getMethodLabel(method) {
            return CarbonCodes.collectionMethod.getLabel(method);
        },
        getAutoCollectInfo(config) {
            if (config.isAutoCollect !== 1) return "";

            let info = `자동 수집: ${config.collectSchedule || "-"}`;
            if (config.collectSchedule === "monthly" && config.collectDay) {
                info += ` (매월 ${config.collectDay}일)`;
            }
            if ((config.collectSchedule === "daily" || config.collectSchedule === "monthly") && config.collectTime) {
                info += ` ${config.collectTime}`;
            }
            const ptAddrs = config.apiParams?.ptAddrs;
            if (Array.isArray(ptAddrs) && ptAddrs.length > 0) {
                info += ` | 관제점 ${ptAddrs.length}개`;
            }
            return info;
        },
    },
};
</script>

<style scoped>
.tag-small {
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
}

.tag-small.method-manual {
    background: #f0fdf4;
    color: #16a34a;
}

.tag-small.method-erp {
    background: #fff7ed;
    color: #ea580c;
}

.tag-small.method-iot {
    background: #f0f9ff;
    color: #0284c7;
}

.tag-small.method-api {
    background: #fdf4ff;
    color: #a855f7;
}

.tag-small.method-file {
    background: #eff6ff;
    color: #2563eb;
}

/* action-buttons 스타일은 전역 _carbon-tables.scss 적용 */

.icon-btn-mini {
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.icon-btn-mini:hover {
    background: #f1f5f9;
    color: #1e293b;
}

.icon-btn-mini.danger:hover {
    background: #fef2f2;
    color: #dc2626;
}

.icon-btn-mini .fa-toggle-on {
    color: #16a34a;
}

.icon-btn-mini .fa-toggle-off {
    color: #94a3b8;
}

.auto-collect-badge {
    padding: 2px 6px;
    border-radius: 3px;
    font-size: 11px;
    font-weight: 500;
}

.auto-collect-badge.ACTIVE {
    background: #f0fdf4;
    color: #16a34a;
}

.auto-collect-badge.inactive {
    background: #f1f5f9;
    color: #94a3b8;
}

.text-center {
    text-align: center;
}

.empty {
    padding: 30px;
    color: #94a3b8;
}

</style>
